import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Bot, MessageCircle, X, ChevronRight, Lock, Trash2, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import MobileNav from '@/components/MobileNav';
import MentionInput from '@/components/MentionInput';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [summonBot, setSummonBot] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyBotSummon, setReplyBotSummon] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  
  const messagesEndRef = useRef(null);
  const threadEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  const isAdmin = user?.roles?.includes('admin');

  const handleCloseThread = async (messageId) => {
    try {
      await axios.post(`${API}/forum/messages/${messageId}/action`, { action: 'close' });
      toast.success('THREAD CLOSED');
      fetchMessages();
      if (activeThread?.parent?.id === messageId) {
        setActiveThread(null);
      }
    } catch (error) {
      toast.error('FAILED TO CLOSE THREAD');
    }
  };

  const handleDeleteThread = async (messageId) => {
    try {
      await axios.post(`${API}/forum/messages/${messageId}/action`, { action: 'delete' });
      toast.success('THREAD DELETED');
      fetchMessages();
      if (activeThread?.parent?.id === messageId) {
        setActiveThread(null);
      }
    } catch (error) {
      toast.error('FAILED TO DELETE THREAD');
    }
  };

  const handleLikeMessage = async (messageId, isLiked) => {
    try {
      if (isLiked) {
        await axios.delete(`${API}/forum/messages/${messageId}/like`);
      } else {
        await axios.post(`${API}/forum/messages/${messageId}/like`);
      }
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === messageId 
          ? { ...m, is_liked: !isLiked, likes_count: (m.likes_count || 0) + (isLiked ? -1 : 1) }
          : m
      ));
      // Also update thread if open
      if (activeThread) {
        setActiveThread(prev => ({
          ...prev,
          parent: prev.parent.id === messageId 
            ? { ...prev.parent, is_liked: !isLiked, likes_count: (prev.parent.likes_count || 0) + (isLiked ? -1 : 1) }
            : prev.parent,
          replies: prev.replies.map(r => 
            r.id === messageId 
              ? { ...r, is_liked: !isLiked, likes_count: (r.likes_count || 0) + (isLiked ? -1 : 1) }
              : r
          )
        }));
      }
    } catch (error) {
      // Silently fail or show error
    }
  };

  useEffect(() => {
    fetchMessages();
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(true);
      if (activeThread) {
        fetchThread(activeThread.parent.id, true);
      }
    }, 7000);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current && !activeThread) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeThread]);

  useEffect(() => {
    if (threadEndRef.current && activeThread) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeThread]);

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/forum/messages`);
      setMessages(response.data);
    } catch (error) {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchThread = async (messageId, silent = false) => {
    if (!silent) setThreadLoading(true);
    try {
      const response = await axios.get(`${API}/forum/messages/${messageId}/replies`);
      setActiveThread(response.data);
    } catch (error) {
      if (!silent) toast.error('Failed to load thread');
    } finally {
      setThreadLoading(false);
    }
  };

  const handleSend = async () => {
    if (!messageText.trim()) return;
    
    const text = messageText.trim();
    const shouldSummonBot = summonBot || text.toLowerCase().includes('@zombiebot');
    
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      sender_id: 'current-user',
      sender_email: 'You',
      sender_role: 'performer',
      text: text,
      created_at: new Date().toISOString(),
      reply_to_id: null,
      reply_count: 0,
      meta: null
    };
    setMessages(prev => [...prev, optimisticMessage]);
    setMessageText('');
    setSummonBot(false);
    
    const botTypingMessage = shouldSummonBot ? {
      id: `typing-${Date.now()}`,
      sender_id: 'bot',
      sender_email: 'zombiebot',
      sender_role: 'bot',
      text: '...',
      created_at: new Date().toISOString(),
      reply_to_id: null,
      reply_count: 0,
      meta: { typing: true }
    } : null;
    
    if (botTypingMessage) {
      setMessages(prev => [...prev, botTypingMessage]);
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/forum/messages`, {
        text: text,
        summon_bot: summonBot
      });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      setMessages(prev => prev.filter(m => !m.id.startsWith('temp-') && !m.id.startsWith('typing-')));
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !activeThread) return;
    
    const text = replyText.trim();
    const shouldSummonBot = replyBotSummon || text.toLowerCase().includes('@zombiebot');
    
    const optimisticReply = {
      id: `temp-${Date.now()}`,
      sender_id: 'current-user',
      sender_email: 'You',
      sender_role: 'performer',
      text: text,
      created_at: new Date().toISOString(),
      reply_to_id: activeThread.parent.id,
      meta: null
    };
    setActiveThread(prev => ({
      ...prev,
      replies: [...prev.replies, optimisticReply]
    }));
    setReplyText('');
    setReplyBotSummon(false);
    
    if (shouldSummonBot) {
      const botTyping = {
        id: `typing-${Date.now()}`,
        sender_id: 'bot',
        sender_email: 'zombiebot',
        sender_role: 'bot',
        text: '...',
        created_at: new Date().toISOString(),
        reply_to_id: activeThread.parent.id,
        meta: { typing: true }
      };
      setActiveThread(prev => ({
        ...prev,
        replies: [...prev.replies, botTyping]
      }));
    }
    
    setSendingReply(true);
    try {
      await axios.post(`${API}/forum/messages`, {
        text: text,
        reply_to_id: activeThread.parent.id,
        summon_bot: replyBotSummon
      });
      fetchThread(activeThread.parent.id);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'NOW';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}M`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}H`;
    return date.toLocaleDateString().toUpperCase();
  };

  const MessageBubble = ({ msg, showThread = true, isInThread = false }) => {
    const isBotMsg = msg.sender_role === 'bot';
    const isAdminMsg = msg.sender_role === 'admin';
    const username = msg.sender_email?.split('@')[0] || 'user';
    const isClosed = msg.is_closed;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`group ${isInThread ? 'pl-4 border-l-4 border-zom-yellow' : ''}`}
      >
        <div className={`border-3 p-4 ${
          isClosed 
            ? 'bg-zom-black/50 border-zom-white/30 opacity-60'
            : isBotMsg 
              ? 'bg-zom-blue border-zom-yellow text-zom-white' 
              : isAdminMsg
                ? 'bg-zom-red border-zom-white text-zom-white'
                : 'bg-zom-black border-zom-white text-zom-white'
        }`}>
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            {isBotMsg ? (
              <div className="w-6 h-6 bg-zom-yellow text-zom-black flex items-center justify-center border-2 border-zom-black">
                <Bot className="w-4 h-4" strokeWidth={2.5} />
              </div>
            ) : (
              <div className={`w-6 h-6 flex items-center justify-center text-xs font-heading border-2 ${
                isAdminMsg ? 'bg-zom-white text-zom-red border-zom-black' : 'bg-zom-yellow text-zom-black border-zom-black'
              }`}>
                {username[0]?.toUpperCase()}
              </div>
            )}
            <span className={`font-heading text-xs tracking-wide ${
              isBotMsg ? 'text-zom-yellow' : isAdminMsg ? 'text-zom-white' : 'text-zom-yellow'
            }`}>
              @{username.toUpperCase()}
            </span>
            <span className="text-zom-white/50 text-xs font-heading">{formatTime(msg.created_at)}</span>
            {msg.meta?.latency_ms && (
              <span className="text-zom-white/30 text-xs font-heading">{msg.meta.latency_ms}MS</span>
            )}
            {isClosed && (
              <span className="ml-auto flex items-center gap-1 text-zom-red text-xs font-heading">
                <Lock className="w-3 h-3" strokeWidth={2.5} />
                CLOSED
              </span>
            )}
          </div>
          
          {/* Content */}
          <p className="font-body text-sm whitespace-pre-wrap leading-relaxed">
            {msg.text}
          </p>
          
          {/* Thread indicator */}
          {showThread && msg.reply_count > 0 && (
            <button
              onClick={() => fetchThread(msg.id)}
              className="mt-3 flex items-center gap-2 text-zom-yellow hover:text-zom-white text-xs font-heading transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
              <span>{msg.reply_count} {msg.reply_count === 1 ? 'REPLY' : 'REPLIES'}</span>
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
          
          {/* Actions row */}
          {showThread && !isInThread && !isClosed && (
            <div className="mt-3 flex items-center gap-2">
              {/* Like button */}
              <button
                onClick={() => handleLikeMessage(msg.id, msg.is_liked)}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs font-heading transition-colors ${
                  msg.is_liked 
                    ? 'text-zom-red' 
                    : 'text-zom-white/50 hover:text-zom-red'
                }`}
              >
                <Heart className={`w-4 h-4 ${msg.is_liked ? 'fill-current' : ''}`} strokeWidth={2.5} />
                {msg.likes_count > 0 && <span>{msg.likes_count}</span>}
              </button>
              
              <button
                onClick={() => fetchThread(msg.id)}
                className="px-3 py-1.5 bg-zom-yellow/20 border border-zom-yellow text-zom-yellow hover:bg-zom-yellow hover:text-zom-black text-xs font-heading transition-colors"
              >
                REPLY
              </button>
              
              {/* Admin controls */}
              {isAdmin && !msg.reply_to_id && (
                <>
                  <button
                    onClick={() => handleCloseThread(msg.id)}
                    className="px-2 py-1.5 text-zom-white/50 hover:text-zom-yellow text-xs font-heading transition-colors ml-auto flex items-center gap-1"
                    title="Close thread"
                  >
                    <Lock className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={() => handleDeleteThread(msg.id)}
                    className="px-2 py-1.5 text-zom-white/50 hover:text-zom-red text-xs font-heading transition-colors flex items-center gap-1"
                    title="Delete thread"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-zom-blue pt-20 pb-24" data-testid="chat-page">
      {/* Bot presence banner */}
      <div className="fixed top-16 left-0 right-0 z-30 bg-zom-black/90 border-b-2 border-zom-yellow px-4 py-2">
        <div className="flex items-center gap-2 text-zom-yellow text-xs font-heading">
          <Bot className="w-4 h-4" strokeWidth={2.5} />
          <span>@ZOMBIEBOT ONLINE</span>
          <div className="w-2 h-2 bg-zom-yellow animate-blink ml-auto" />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex pt-8" style={{ height: 'calc(100vh - 240px)' }}>
        {/* Messages list */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 transition-all ${activeThread ? 'hidden md:block md:w-1/2' : 'w-full'}`}>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-zom-yellow border-t-transparent animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="w-20 h-20 text-zom-yellow/30 mx-auto mb-4" strokeWidth={2} />
              <p className="text-zom-white font-heading mb-2">NO MESSAGES</p>
              <p className="text-zom-white/60 text-sm font-heading">START THE CONVERSATION!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Thread panel */}
        <AnimatePresence>
          {activeThread && (
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              className="fixed md:relative inset-0 md:inset-auto md:w-1/2 bg-zom-black md:border-l-4 border-zom-yellow flex flex-col z-50"
            >
              {/* Thread header */}
              <div className="sticky top-0 bg-zom-black border-b-4 border-zom-yellow p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-zom-white font-heading tracking-wide">THREAD</h2>
                  <button
                    onClick={() => setActiveThread(null)}
                    className="text-zom-white hover:text-zom-yellow p-1"
                  >
                    <X className="w-6 h-6" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Thread messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {threadLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-zom-yellow border-t-transparent animate-spin" />
                  </div>
                ) : (
                  <>
                    <MessageBubble msg={activeThread.parent} showThread={false} />
                    {activeThread.replies.map((reply) => (
                      <MessageBubble key={reply.id} msg={reply} showThread={false} isInThread />
                    ))}
                  </>
                )}
                <div ref={threadEndRef} />
              </div>

              {/* Thread reply input */}
              <div className="border-t-4 border-zom-yellow p-4 bg-zom-black">
                <div className="flex items-center gap-2 mb-2">
                  <Switch
                    checked={replyBotSummon}
                    onCheckedChange={setReplyBotSummon}
                    id="reply-summon-bot"
                    className="data-[state=checked]:bg-zom-yellow"
                  />
                  <label htmlFor="reply-summon-bot" className="text-zom-white/60 text-xs font-heading flex items-center gap-1">
                    <Bot className="w-3 h-3" strokeWidth={2.5} />
                    SUMMON BOT
                  </label>
                </div>
                <div className="flex gap-2">
                  <MentionInput
                    value={replyText}
                    onChange={setReplyText}
                    onSubmit={handleReply}
                    placeholder="REPLY... (use @username to tag)"
                    className="flex-1 bg-zom-black border-2 border-zom-white text-zom-white text-sm px-4 py-3 font-body placeholder:text-zom-white/40"
                    singleLine
                  />
                  <button
                    onClick={handleReply}
                    disabled={sendingReply || !replyText.trim()}
                    className="px-4 bg-zom-yellow text-zom-black border-2 border-zom-black disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main input - positioned above bottom nav */}
      <div className="fixed bottom-20 left-0 right-0 border-t-4 border-zom-yellow p-4 bg-zom-black">
        <div className="flex items-center gap-3 mb-2">
          <Switch
            checked={summonBot}
            onCheckedChange={setSummonBot}
            id="main-summon-bot"
            className="data-[state=checked]:bg-zom-yellow"
          />
          <label htmlFor="main-summon-bot" className="text-zom-white/60 text-xs font-heading flex items-center gap-1">
            <Bot className="w-4 h-4" strokeWidth={2.5} />
            SUMMON @ZOMBIEBOT
          </label>
        </div>
        <div className="flex gap-2">
          <MentionInput
            value={messageText}
            onChange={setMessageText}
            onSubmit={handleSend}
            placeholder="MESSAGE... (use @username to tag)"
            className="flex-1 bg-zom-black border-3 border-zom-white text-zom-white px-4 py-3 font-body placeholder:text-zom-white/40"
            data-testid="chat-input"
            singleLine
          />
          <button
            onClick={handleSend}
            disabled={sending || !messageText.trim()}
            className="px-6 bg-zom-red text-zom-white border-3 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow disabled:opacity-50"
            data-testid="send-btn"
          >
            <Send className="w-6 h-6" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <MobileNav />
    </div>
  );
};

export default ChatPage;
